import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UtilsService } from '../core/utils/utils.service';
import { Task, TaskWorkingStatus } from './task.model';
import { DbService } from '../core/db/db.service';
import { LoggerService } from '../core/logger/logger.service';
import { Subscription } from 'rxjs';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { AuthService } from '../auth/auth.service';
import { NotificationService } from '../core/notification/notification.service';
import { TasksService } from './tasks.service';
import { TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../settings/settings.service';
import { TaskDetailsComponent } from './task-details/task-details.component';
import { Router } from '@angular/router';
import { Popover } from '@ionic/core/dist/types/components/popover/popover';
import { ProjectService } from '../projects/project.service';
import { SyncService } from '../core/sync/sync.service';

@Component({
    selector: 'app-tasks',
    templateUrl: './tasks.page.html',
    styleUrls: ['./tasks.page.scss'],
    providers: [
        {
            // See the Moment.js docs for the meaning of these formats:
            // https://momentjs.com/docs/#/displaying/format/
            provide: MAT_DATE_FORMATS,
            useValue: {
                display: {
                    dateInput: 'DD.MM.YYYY',
                    monthYearLabel: 'MMM YYYY',
                    dateA11yLabel: 'LL',
                    monthYearA11yLabel: 'MMMM YYYY',
                },
            },
        },
    ],
})
export class TasksPage implements OnInit, OnDestroy {
    @Input() projectId: string = null; // only used if we are in the project page (to only show the tasks of the current project)
    @Input() filterVisible = true;

    @ViewChild(TaskDetailsComponent) taskDetailsComponent: TaskDetailsComponent;

    tasksSubscription: Subscription;
    tasksLoadingSubscription: Subscription;
    tasks: Task[] = [];
    tempTasks: Task[] = []; // used for sorting and filtering
    tasksLoading = true;

    projectList: { _id: string; name: string; projectNumber: string }[] = [];
    projectListLoadingSubscription: Subscription;

    // filter
    @ViewChild('tasksFilterPopover') tasksFilterPopover: Popover;
    projectModalVisible = false;
    currentFilteredProjects: Array<string> = []; // the actual confirmed filtered projects, used for actual filtering

    workingStatuses = {
        open: {
            color: 'danger',
            severity: 'danger',
        },
        started: {
            color: 'warning',
            severity: 'warn',
        },
        done: {
            color: 'success',
            severity: 'success',
        },
    };

    currentOpenTasks: Task[] = [];
    futureOpenTasks: Task[] = [];
    doneTasks: Task[] = [];

    constructor(
        public utils: UtilsService,
        public authSrv: AuthService,
        private db: DbService,
        private notify: NotificationService,
        private taskSrv: TasksService,
        private logger: LoggerService,
        private translate: TranslateService,
        private router: Router,
        private syncSrv: SyncService,
        public projectSrv: ProjectService,
        public settingsSrv: SettingsService
    ) {}

    ngOnInit(): void {
        const state = this.router.getCurrentNavigation()?.extras.state;
        if (state?.['projectId']) {
            this.projectId = state?.['projectId'];
            history.replaceState({ navigationId: state['navigationId'] }, ''); // replace state to prevent active filter after reload
        }

        // load project list initial
        this.loadProjectList();

        // subscribe to task list changes
        this.tasksSubscription = this.taskSrv.currentTaskList$.subscribe((taskList) => {
            this.tasks = taskList.filter((x) => !x.archivedAt);
            this.filterTaskList();
        });

        this.tasksLoadingSubscription = this.taskSrv.taskListLoading$.subscribe((loading) => {
            this.tasksLoading = loading;
        });

        // subscribe to load project list event
        this.projectListLoadingSubscription = this.taskSrv.loadProjectList$.subscribe(() => {
            this.loadProjectList();
        });
    }

    ngOnDestroy(): void {
        this.tasksSubscription?.unsubscribe();
        this.tasksLoadingSubscription?.unsubscribe();
        this.projectListLoadingSubscription?.unsubscribe();
    }

    /**
     * Load project-list from local db
     */
    loadProjectList() {
        this.projectSrv
            .getProjectsOfFolder(null, true)
            .then(async (projects) => {
                this.projectList = projects.map((x) => {
                    return {
                        _id: x._id,
                        name: x.name,
                        projectNumber: x.projectNumber,
                    };
                });
            })
            .catch((err) => {
                this.logger.error('[CALENDAR] could not load project list', err);
            });
    }

    /**
     * Creates a new task
     */
    onCreateNewTask(): void {
        if (this.taskDetailsComponent) {
            this.taskDetailsComponent.openCreateTaskModal(false, { projectId: this.projectId });
        }
    }

    /**
     * Edits a task
     * @param task the task data
     * @param viewMode the view mode of the task (edit or view)
     * @param event
     */
    async onEditTask(task: Task, viewMode: 'edit' | 'view', event: any): Promise<void> {
        event.stopPropagation();

        // open edit task modal
        if (this.taskDetailsComponent) {
            await this.taskDetailsComponent.openEditTaskModal(task, viewMode);
        }
    }

    /**
     * Deletes an existing task
     * @param task
     * @param event
     */
    onDeleteTask(task: Task, event: any) {
        event.stopPropagation();

        this.notify
            .confirm(
                this.translate.instant('tasks.info.delete-task-message'),
                this.translate.instant('tasks.info.delete-task-title'),
                this.translate.instant('shared.delete')
            )
            .then(() => {
                this.db.delete(task._id).subscribe(
                    async (res) => {
                        let taskSharings: any = await this.db.getItemsByRelationAndType(`task#${task._id}`, 'task-sharing').toPromise();
                        for (let sharing of taskSharings) {
                            await this.db.delete(sharing._id).toPromise();
                        }

                        this.logger.log('[TASKS PAGE] deleted task', res);
                        this.notify.success(this.translate.instant('tasks.info.deleted-task'));

                        // now handle visibility of project
                        if (this.settingsSrv.getUserRights().canManageTasks) {
                            await this.checkVisibilityOfProject(task.relatedProject);
                        }
                    },
                    (err) => {
                        this.logger.log('[TASKS PAGE] could not delete task', err);
                        this.notify.error(this.translate.instant('tasks.error.delete-task'));
                    }
                );
            })
            .catch(() => {
                // cancel button
            });
    }

    /**
     * Archives an existing task
     * @param task
     * @param event
     */
    onArchiveTask(task: Task, event: any) {
        event.stopPropagation();

        this.notify
            .confirm(
                this.translate.instant('tasks.info.archive-task-message'),
                this.translate.instant('tasks.info.archive-task-title'),
                this.translate.instant('shared.archive')
            )
            .then(async () => {
                try {
                    let taskSharings: any = await this.db.getItemsByRelationAndType(`task#${task._id}`, 'task-sharing').toPromise();
                    for (let sharing of taskSharings) {
                        await this.db.delete(sharing._id).toPromise();
                    }

                    // update task in db
                    task.archivedAt = Date.now();
                    let updateRes = await this.db.put(task).toPromise();

                    this.logger.log('[TASKS PAGE] archived task', updateRes);
                    this.notify.success(this.translate.instant('tasks.info.archived-task'));

                    // now handle visibility of project
                    if (this.settingsSrv.getUserRights().canManageTasks) {
                        await this.checkVisibilityOfProject(task.relatedProject);
                    }
                } catch (err) {
                    this.logger.log('[TASKS PAGE] could not archive task', err);
                    this.notify.error(this.translate.instant('tasks.error.archive-task'));
                }
            })
            .catch(() => {
                // cancel button
            });
    }

    /**
     * Get name of status
     */
    getStatusName(workingStatus: TaskWorkingStatus) {
        return this.translate.instant(`tasks.status.${workingStatus}`);
    }

    /**
     * Get the project info by id
     */
    getProjectInfoById(projectId: string) {
        if (projectId == null) return null;
        const found = this.projectList.find((x) => x._id == projectId);
        return found ? found : null;
    }

    /**
     * Go's to report generation for the related job and extensions
     */
    async generateReportForTask(task: Task, $event: any): Promise<void> {
        $event.stopPropagation();

        // check if the user has access to the project
        if (!(await this.projectSrv.hasProjectAccess(task.relatedProject))) {
            this.notify.error(this.translate.instant('tasks.info.project-not-shared'));
            return;
        }

        this.projectSrv.currentlySelectedJobsFromTask = { taskId: task._id, count: task.jobs.length };
        this.projectSrv.currentlySelectedJobs = []; // reset this (we not allow generating different partial reports)
        this.router.navigate(['/project', task.relatedProject, 'reports']);
    }

    /**
     * When a user confirms the project selection
     * @param projectIds
     */
    onConfirmProjectSelection(projectIds: string[]): void {
        this.currentFilteredProjects = [...projectIds];
        this.filterTaskList();
        this.projectModalVisible = false;
    }

    /**
     * When a user removes a single project from the filter selection
     */
    removeProjectFromFilterSelection(projectId: string, $event: any): void {
        $event.stopPropagation();
        this.currentFilteredProjects = this.currentFilteredProjects.filter((x) => x !== projectId);
    }

    /**
     * Open filter popover
     * @param event
     */
    async openFilterPopover(event: any) {
        await this.tasksFilterPopover.present(event);
    }

    /**
     * Open the project selection modal (for filter)
     */
    openProjectSelectionModal(): void {
        this.tasksFilterPopover.dismiss();
        this.projectModalVisible = true;
    }

    /**
     * Confirm current set filters
     */
    confirmCurrentFilters(): void {
        this.tasksFilterPopover.dismiss();
        this.filterTaskList();
    }

    /**
     * Resets current set filters
     */
    resetCurrentFilters(): void {
        this.currentFilteredProjects = [];
        this.projectId = null;
        this.filterTaskList();
        this.tasksFilterPopover.dismiss();
    }

    /**
     * Filter the task list based on the current filters
     */
    filterTaskList(): void {
        if (this.projectId != null) {
            this.currentFilteredProjects = [this.projectId];
        }

        this.tempTasks = this.utils.clone(this.tasks);

        // check if there are active filters
        if (this.currentFilteredProjects.length > 0) {
            this.tempTasks = this.tempTasks.filter((x) => {
                return this.currentFilteredProjects.includes(x.relatedProject);
            });
        }

        this.partitionTasks();
    }

    private partitionTasks(): void {
        const now = Date.now();

        this.doneTasks = this.tempTasks.filter((t) => t.workingStatus === 'done');

        const openTasks = this.tempTasks.filter((t) => t.workingStatus !== 'done');
        this.currentOpenTasks = openTasks.filter((t) => !this.isFutureTask(t, now));
        this.futureOpenTasks = openTasks.filter((t) => this.isFutureTask(t, now));
    }

    private isFutureTask(task: Task, now: number): boolean {
        return task.startDate != null && task.startDate > now;
    }

    protected isOverdue(task: Task): boolean {
        return task.deadlineDate != null && task.deadlineDate < Date.now();
    }

    /**
     * Checks if we have to hide the project in our project list after some actions (eg. delete or archive task)
     * @param projectId the project id to get all related tasks
     */
    async checkVisibilityOfProject(projectId: string) {
        let projectView = this.projectId != null;

        // if we are in project view and the guest access is enabled, we need to check if there are still relevant tasks in the project
        // if we are not in project view, we need to check if the user has access to the project (project-sharings)
        try {
            if (
                (projectId != null && projectView && this.projectSrv.currentProjectGuestAccess) ||
                (projectId != null && !projectView && !(await this.projectSrv.hasProjectAccess(projectId)))
            ) {
                const tasks: Task[] = (await this.db.getItemsByType('task').toPromise()) as Task[];
                const hasTasks = tasks.some((task) => task.relatedProject === projectId && !task.archivedAt);

                // check if there are relevant tasks in the project (otherwise -> navigate user to dashboard)
                if (!hasTasks) {
                    if (projectView) {
                        // navigate user to the dashboard and set the current project to null
                        await this.router.navigate(['/'], { replaceUrl: true });
                        this.projectSrv.setCurrentProject(null);

                        this.notify.success(this.translate.instant('tasks.details.info.project-access-removed'), null, true, 4000);
                    }

                    // remove the project from our list
                    await this.syncSrv.deleteProjectData(projectId, true);
                }
            }
        } catch (checkProjectTasksErr) {
            this.logger.error('[TASKS PAGE] failed: check and handling for relevant tasks in the project', checkProjectTasksErr);
            this.utils.sentryCaptureMessage('failed: check and handling for relevant tasks in the project', checkProjectTasksErr);
        }
    }
}
